import {
  Badge,
  Button,
  Card,
  Flex,
  HStack,
  InfoBox,
  Select,
  Stack,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdCheckCircle, MdError, MdList, MdVisibility } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  AiExecutionLog,
  logsService,
  PaginatedResult,
} from "services/logsService";
import { getThemeColor } from "theme/colors";

interface AiCallsPanelProps {
  /** Bumped by the page's Refresh button. */
  refreshToken: number;
}

/**
 * One row per AI call (`AiExecutionLog`) — the list tab of Analytics → AI actions. The per-turn
 * grouping of the same rows is the Activity tab (`TracesPanel`).
 */
const AiCallsPanel = ({ refreshToken }: AiCallsPanelProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AiExecutionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [agentFilter, setAgentFilter] = useState("");
  const pageSize = 20;

  const WIDGET_AGENT_ROUTES = ["support", "analytics", "copilot", "general"];

  useEffect(() => {
    setPage(1);
    loadLogs(1, agentFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFilter]);

  useEffect(() => {
    loadLogs(page, agentFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, refreshToken]);

  const loadLogs = async (pageToLoad: number, filter?: string) => {
    try {
      setLoading(true);
      const data: PaginatedResult<AiExecutionLog> =
        await logsService.getAllLogs(pageToLoad, pageSize, filter || undefined);
      setLogs(data.items);
      setTotal(data.total);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.logs.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;

  if (loading) {
    return (
      <Stack gap={4}>
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color={{ base: "gray.600", _dark: "white" }}
            >
              {t("app.logs.loading")}
            </Text>
          </Card.Body>
        </Card.Root>
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <Flex justify="flex-end" align="center" wrap="wrap" gap={3}>
        <HStack gap={3}>
          <Select
            items={[
              { label: t("app.logs.allAgents"), value: "" },
              ...WIDGET_AGENT_ROUTES.map((route) => ({
                label: route,
                value: route,
              })),
            ]}
            value={[agentFilter]}
            onValueChange={(details) => setAgentFilter(details.value[0] ?? "")}
            width="180px"
            size="sm"
          />
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
        overflow="hidden"
      >
        <Card.Body p={0}>
          {loading ? (
            <Flex p={8} justify="center">
              <Text>{t("app.logs.loading")}</Text>
            </Flex>
          ) : logs.length === 0 ? (
            <VStack gap={4} py={8}>
              <MdList size={64} color="gray" />
              <Text
                fontSize="lg"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.logs.noLogs")}
              </Text>
              <Text
                fontSize="sm"
                color={{ base: "gray.500", _dark: "gray.500" }}
                textAlign="center"
              >
                {t("app.logs.noLogsDescription")}
              </Text>
            </VStack>
          ) : (
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t("app.logs.status")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>{t("app.logs.agent")}</Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.logs.provider")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>{t("app.logs.model")}</Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.logs.userId")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.logs.requestSize")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.logs.responseSize")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.logs.duration")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.logs.executedAt")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.logs.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {logs.map((log) => (
                  <Table.Row
                    key={log.id}
                    cursor="pointer"
                    onClick={() => navigate(`/admin/ai-logs/${log.id}`)}
                  >
                    <Table.Cell>
                      <HStack>
                        {log.isSuccess ? (
                          <MdCheckCircle color="green" size={20} />
                        ) : (
                          <MdError color="red" size={20} />
                        )}
                        <Badge
                          colorPalette={log.isSuccess ? "green" : "red"}
                          variant="outline"
                        >
                          {log.httpStatusCode || t("app.common.notAvailable")}
                        </Badge>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      {log.agentRoute ? (
                        <Badge colorPalette="blue" variant="subtle" size="sm">
                          {log.agentRoute}
                        </Badge>
                      ) : (
                        <Text
                          fontSize="xs"
                          color={{ base: "gray.400", _dark: "gray.600" }}
                        >
                          —
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" fontWeight="medium">
                        {log.llmProvider}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" fontFamily="mono">
                        {log.llmModel}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" fontFamily="mono">
                        {log.userId
                          ? log.userId.substring(0, 8) + "..."
                          : t("app.common.notAvailable")}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {formatBytes(log.requestLength)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {formatBytes(log.responseLength)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {formatDuration(log.durationMs)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{formatDate(log.executedAt)}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/ai-logs/${log.id}`);
                        }}
                        size="sm"
                        variant="ghost"
                        colorPalette="blue"
                        title={t("app.logs.viewDetails")}
                      >
                        <MdVisibility />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Card.Body>
      </Card.Root>

      {total > 0 && (
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.pagination.showing", {
              start: start + 1,
              end: Math.min(end, total),
              total,
            })}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("app.pagination.previous")}
            </Button>
            <Text fontSize="sm">
              {t("app.pagination.pageInfo", {
                current: currentPage,
                total: totalPages,
              })}
            </Text>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("app.pagination.next")}
            </Button>
          </HStack>
        </HStack>
      )}
    </Stack>
  );
};

export default AiCallsPanel;
